import { Col, DatePicker, Form, Input, InputNumber, Row, Select } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

const AddNewSeriesForm = ({ stages, handleSetFormRef, series, linkedFixtures }) => {
  const { formatMessage } = useIntl();
  const [form] = Form.useForm();
  const [seriesType, setSeriesType] = useState(series ? series?.seriesType : "BEST_OF");

  useEffect(() => {
    if (seriesType === "HOME_AND_AWAY") {
      form.setFieldValue("maxFixturesNumber", 2);
    }

    if (seriesType === "KNOCKOUT") {
      form.setFieldValue("maxFixturesNumber", 1);
    }
  }, [seriesType, form]);

  const initValues = series
    ? {
        ...series,
        startDate: series?.startDate ? dayjs(series?.startDate) : null,
        endDate: series?.endDate ? dayjs(series?.endDate) : null,
      }
    : {
        status: "ACTIVE",
        seriesType: "BEST_OF",
      };

  useEffect(() => {
    handleSetFormRef(form);
  }, [handleSetFormRef, form]);

  const handleChangeForm = (value) => {
    setSeriesType(value);
  };

  return (
    <Form
      form={form}
      initialValues={initValues}
      colon={false}
      labelWrap={true}
      labelCol={{
        span: 11,
      }}
      wrapperCol={{
        span: 15,
      }}
    >
      <Row>
        <Col span={12}>
          <Form.Item
            required={true}
            name={"nameLocal"}
            label={formatMessage({
              id: "series.name",
              defaultMessage: "Series Name",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.local.name.item",
                defaultMessage: "The name of the {item} in the local Language",
              },
              {
                item: formatMessage({
                  id: "series",
                  defaultMessage: "Series",
                }),
              },
            )}
            rules={[
              {
                required: true,
                message: "Please input Series Name",
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            required={true}
            name={"seriesType"}
            label={formatMessage({
              id: "series.type",
              defaultMessage: "Series Type",
            })}
            tooltip={formatMessage({
              id: "form.help.series.type",
              defaultMessage: "The type of Series",
            })}
            rules={[
              {
                required: true,
                message: "Please input Series Type",
              },
            ]}
          >
            <Select disabled={linkedFixtures} onChange={handleChangeForm}>
              <Select.Option value={"BEST_OF"}>Best Of</Select.Option>
              <Select.Option value={"HOME_AND_AWAY"}>Home and Away</Select.Option>
              <Select.Option value={"KNOCKOUT"}>Knockout</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            labelCol={{
              span: 15,
            }}
            required={seriesType === "BEST_OF"}
            rules={[
              {
                required: seriesType === "BEST_OF",
                message: "Please input Number of Fixtures",
              },
            ]}
            name={"maxFixturesNumber"}
            label={formatMessage({
              id: "max.fixtures.number",
              defaultMessage: "Number of Fixtures",
            })}
            tooltip={formatMessage({
              id: "form.help.series.number.of.fixtures",
              defaultMessage: "The maximum number of games within a playoff series",
            })}
          >
            <InputNumber disabled={seriesType !== "BEST_OF" || linkedFixtures} style={{ width: "100%" }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={"stageCode"}
            label={formatMessage({
              id: "stage",
              defaultMessage: "Stage",
            })}
            tooltip={formatMessage({
              id: "form.help.series.stage",
              defaultMessage: "Which stage is this series linked to?",
            })}
          >
            <Select allowClear={true} disabled={linkedFixtures}>
              {stages?.map((stage) => (
                <Select.Option key={stage?.stageCode} value={stage?.stageCode}>
                  {stage?.nameLocal}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            required={true}
            rules={[
              {
                required: true,
                message: "Please input Status",
              },
            ]}
            name={"status"}
            label={formatMessage({
              id: "status",
              defaultMessage: "Status",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.status.item",
                defaultMessage: "What is the current status of this {item}?",
              },
              {
                item: formatMessage({
                  id: "series",
                  defaultMessage: "Series",
                }),
              },
            )}
          >
            <Select>
              <Select.Option value={"ACTIVE"}>Active</Select.Option>
              <Select.Option value={"COMPLETE"}>Complete</Select.Option>
              <Select.Option value={"NOT_STARTED"}>Not Started</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name={"startDate"}
            label={formatMessage({
              id: "date.start",
              defaultMessage: "Start Date",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.start.date.item",
                defaultMessage: "When will this {item} start?",
              },
              {
                item: formatMessage({
                  id: "series",
                  defaultMessage: "Series",
                }),
              },
            )}
          >
            <DatePicker style={{ width: "100%" }} format={"DD/MM/YYYY"} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            style={{ marginBottom: 0 }}
            name={"endDate"}
            label={formatMessage({
              id: "date.end",
              defaultMessage: "End Date",
            })}
            tooltip={formatMessage(
              {
                id: "form.help.end.date.item",
                defaultMessage: "When will this {item} end?",
              },
              {
                item: formatMessage({
                  id: "series",
                  defaultMessage: "Series",
                }),
              },
            )}
          >
            <DatePicker style={{ width: "100%" }} format={"DD/MM/YYYY"} />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default AddNewSeriesForm;
